// Scripts — route /scripts
//
// Lightweight project list. Routes the operator into the editor for a given
// script. We intentionally avoid a "New script" CTA here — the create flow
// belongs on the Dashboard / topic-spike surface.

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Pencil } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import { formatTime } from '@/lib/utils'
import { Badge, Card, EmptyState, Table } from '@/admin-design/_common'

export default function Scripts() {
  const scriptsQ = useQuery({
    queryKey: ['scripts'],
    queryFn: api.listScripts,
  })

  return (
    <div className="space-y-4">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-foreground">Scripts</h1>
        <span className="text-sm text-muted-foreground">
          {scriptsQ.data?.length ?? 0}개
        </span>
      </header>

      <Card className="overflow-hidden">
        {scriptsQ.isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">불러오는 중…</p>
        ) : scriptsQ.isError ? (
          <p className="p-6 text-sm text-destructive">
            로드 실패: {errorMessage(scriptsQ.error)}
          </p>
        ) : !scriptsQ.data || scriptsQ.data.length === 0 ? (
          <EmptyState
            title="아직 등록된 스크립트가 없어요."
            description="Dashboard에서 트렌드 토픽을 클릭해 새 스크립트를 만들어보세요."
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Header>Title</Table.Header>
                  <Table.Header>Channel</Table.Header>
                  <Table.Header>Topic</Table.Header>
                  <Table.Header>Status</Table.Header>
                  <Table.Header>Updated</Table.Header>
                  <Table.Header className="w-16 text-right">Open</Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {scriptsQ.data.map((p) => (
                  <Table.Row key={p.id}>
                    <Table.Cell className="font-medium text-card-foreground">
                      <Link
                        to={`/scripts/${p.id}`}
                        className="hover:text-info hover:underline"
                      >
                        {p.title || `script #${p.id}`}
                      </Link>
                    </Table.Cell>
                    <Table.Cell className="text-muted-foreground">
                      {p.channel_profile_name || `#${p.channel_profile_id}`}
                    </Table.Cell>
                    <Table.Cell className="text-muted-foreground truncate max-w-[200px]">
                      {p.topic_title || p.topic_id || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge className="font-mono text-[11px] rounded">{p.status}</Badge>
                    </Table.Cell>
                    <Table.Cell className="text-xs text-muted-foreground">
                      {formatTime(p.updated_at)}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex justify-end">
                        <Link
                          to={`/scripts/${p.id}`}
                          className="p-1 text-muted-foreground hover:text-info"
                          aria-label="open editor"
                        >
                          <Pencil size={14} />
                        </Link>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Card>
    </div>
  )
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'unknown error'
}
